/* כל השינויים בנתונים עוברים כאן: שמירה ב-Supabase, עדכון המצב המקומי, וציור מחדש.
   החישובים עצמם נמצאים ב-domain/. */
import { S, SETTINGS, touch, applySettingsRows, resetSettings } from '../domain/state.js';
import { sb, q } from '../lib/supabase.js';
import { noTags, mNorm } from '../lib/text.js';
import { iso10, fMoney, absAmt } from '../lib/format.js';
import { getKey, curKey, keyAdd, keyOfISO, periodShort } from '../domain/periods.js';
import { getTxSrc, CARD_RX, findDupTx } from '../domain/tx.js';
import { cardPeriodSums, cardEffISO, cardChargeDay, cardChargeISOOfKey, cardCurKey } from '../domain/card.js';
import { staleCommits, realizedCommits, commitsFromScan } from '../domain/commitments.js';
import { exitPlan, exitEndUpdate } from '../domain/plan.js';
import { revCandidates } from '../domain/merchants.js';
import { SAV_RX } from '../domain/savings.js';
import { COLORS } from '../domain/categories.js';
import { budgetRows } from '../domain/budgets.js';
import { txExtraFields } from '../domain/scan/reconcile.js';
import { toast, setLoading, ls, UI, setUi } from './ui.js';

const db = () => sb();

/* ══════ טעינה ══════ */

/** Supabase מחזיר עד 1000 שורות בבקשה. בלי דפדוף, החודשים הישנים נעלמים בשקט */
async function loadAllTransactions() {
  const PAGE = 1000, out = [];
  for (let off = 0; ; off += PAGE) {
    const rows = await q(db().from('transactions').select('*').order('date', { ascending: false }).order('id', { ascending: false }).range(off, off + PAGE - 1));
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}
const soft = (p) => q(p).catch(() => []);

export async function loadAll() {
  setLoading(15);
  try {
    const [tx, members, budgets, rec, cats, savings, commits, settings] = await Promise.all([
      loadAllTransactions(),
      soft(db().from('family_members').select('*').order('created_at')),
      q(db().from('budget_goals').select('*')),
      q(db().from('recurring').select('*').order('created_at')),
      q(db().from('categories').select('*')),
      soft(db().from('savings_goals').select('*').order('created_at')),
      soft(db().from('commitments').select('*').order('due_date')),
      soft(db().from('settings').select('*')),
    ]);
    // תיאור מקובץ בנק לא אמור להכיל < או >
    tx.forEach((t) => { if (t.description) t.description = noTags(t.description); if (t.raw_name) t.raw_name = noTags(t.raw_name); });
    Object.assign(S, { tx, members, budgets, rec, cats, savings, commits });
    resetSettings();
    applySettingsRows(settings);
    UI.viewKey = curKey();
    touch();
    setLoading(100);
    await pruneCommits();
    await migrateLocalFlags();
    await fixCardSettlements();
    recordExitEnd();
  } catch (e) {
    setLoading(100);
    console.error(e);
    toast('⚠️ הטעינה מהענן נכשלה: ' + e.message + '. בדוק חיבור ונסה לרענן');
    throw e;
  }
}

/* ══════ הגדרות ══════ */

/** שמירת הגדרה: מעדכן מיד את המצב, ושומר ברקע */
export async function saveSetting(key, value) {
  const d = SETTINGS[key];
  if (!d) throw new Error('הגדרה לא מוכרת: ' + key);
  S.set[d.field] = value;
  touch();
  try {
    await q(db().from('settings').upsert({ key, value: d.ser(value) }));
  } catch (e) {
    toast('⚠️ ההגדרה לא נשמרה בענן (' + key + '): ' + e.message);
  }
}

export function saveBalance(amount, date, eod) {
  return Promise.all([saveSetting('bal_amount', amount), saveSetting('bal_date', date), saveSetting('bal_eod', !!eod)]);
}

export async function saveMonthStart(d) {
  if (!d || d < 1 || d > 28) { toast('יום בין 1 ל-28'); return null; }
  if (d === S.set.monthStart) return null;
  const oldMs = S.set.monthStart > 1 ? S.set.monthStart : 1, cd = cardChargeDay();
  await saveSetting('month_start', d);
  setUi({ viewKey: curKey() });
  // אשראי: אותו מחזור חיוב, אבל המפתח שלו הוא התקופה שבה נופל יום החיוב, והיא תלויה בתחילת החודש.
  // בלי זה, אחרי שינוי מ-1 ל-11 החיוב של 10.11 היה מחושב מהדוח של 10.10
  const cmoves = {};
  S.tx.forEach((t) => {
    if (getTxSrc(t) !== 'credit' || !/^\d{4}-\d\d$/.test(t.month || '')) return;
    const k = keyOfISO(cardChargeISOOfKey(t.month, oldMs, cd));
    if (k !== t.month) (cmoves[k] = cmoves[k] || []).push(t);
  });
  const cn = Object.values(cmoves).reduce((s, a) => s + a.length, 0);
  if (cn) await rekeyTx(cmoves, cn);
  // פעולות עו"ש שכבר שמורות נשארות בתקופה הישנה עד שמעדכנים אותן
  const moves = {};
  S.tx.forEach((t) => {
    if (getTxSrc(t) === 'credit' || !t.date) return;
    const k = keyOfISO(t.date);
    if (k !== t.month) (moves[k] = moves[k] || []).push(t);
  });
  const n = Object.values(moves).reduce((s, a) => s + a.length, 0);
  return n ? { n, apply: () => rekeyTx(moves, n) } : null;
}
async function rekeyTx(moves, n) {
  try {
    await Promise.all(Object.keys(moves).map(async (k) => {
      const ids = moves[k].map((t) => t.id);
      await q(db().from('transactions').update({ month: k }).in('id', ids));
      moves[k].forEach((t) => (t.month = k));
    }));
    touch();
    toast('✅ ' + n + ' פעולות עודכנו');
  } catch (e) {
    toast('⚠️ העדכון נכשל: ' + e.message);
  }
}

export function setCardManual(amount) {
  if (!(amount > 0)) return saveSetting('card_manual', { amount: 0, key: '' });
  return saveSetting('card_manual', { amount, key: cardCurKey() });
}

export function setExitTarget(iso) {
  saveSetting('exit_target', iso || '');
  const P = exitPlan();
  saveSetting('exit_stage', P ? P.group : '');
}
export function setBuffer(v) {
  return saveSetting('buf_target', Number(v) || 0);
}
export function recordExitEnd() {
  const h = exitEndUpdate();
  if (h) saveSetting('exit_end', h);
}

/* ══════ פעולות ══════ */

let extraOk = true;
/** אם עמודות הזיהוי חסרות בטבלה, שומר בלעדיהן */
export async function insertTx(body, scanItem) {
  body = Object.assign({}, body, { description: noTags(body.description) });
  const b = Object.assign({}, body, extraOk && scanItem ? txExtraFields(scanItem) : {});
  try {
    const rows = await q(db().from('transactions').insert(b).select());
    return addLocalTx(rows[0]);
  } catch (e) {
    if (extraOk && scanItem && /ref|raw_name|card4|orig_amount|column|schema/i.test(e.message)) {
      extraOk = false;
      toast('עמודות הזיהוי עוד לא נוספו ב-Supabase. נשמר בלעדיהן');
      const rows = await q(db().from('transactions').insert(body).select());
      return addLocalTx(rows[0]);
    }
    throw e;
  }
}
function addLocalTx(t) {
  S.tx.unshift(t);
  return t;
}

export async function updateTx(id, patch, quiet) {
  const t = S.tx.find((x) => x.id === id);
  const rows = await q(db().from('transactions').update(patch).eq('id', id).select());
  if (t) Object.assign(t, rows[0] || patch);
  if (!quiet) touch();
  return t;
}

export async function deleteTx(id) {
  await q(db().from('transactions').delete().eq('id', id));
  S.tx = S.tx.filter((t) => t.id !== id);
  touch();
}

export async function deleteTxs(ids) {
  const CH = 100;
  for (let i = 0; i < ids.length; i += CH) {
    const part = ids.slice(i, i + CH);
    await q(db().from('transactions').delete().in('id', part));
    const set = new Set(part);
    S.tx = S.tx.filter((t) => !set.has(t.id));
  }
  touch();
}

export async function toggleExclude(t) {
  const next = !(t.excluded === true);
  t.excluded = next;
  touch();
  toast(next ? 'הוחרג מסכום' : 'הוחזר לסכום');
  try {
    await q(db().from('transactions').update({ excluded: next }).eq('id', t.id));
  } catch (e) {
    t.excluded = !next;
    touch();
    toast('⚠️ לא נשמר: ' + e.message);
  }
}

export async function moveTxPeriod(t, k) {
  await updateTx(t.id, { month: k });
  toast('✅ הועברה לתקופה ' + periodShort(k));
}

/** שינוי קטגוריה לכמה פעולות (סיווג עסק) */
export async function setTxCat(ids, catId) {
  const CH = 80;
  for (let i = 0; i < ids.length; i += CH) await q(db().from('transactions').update({ cat: catId }).in('id', ids.slice(i, i + CH)));
  const set = new Set(ids);
  S.tx.forEach((t) => { if (set.has(t.id)) t.cat = catId; });
  touch();
}

export async function copyPeriod(fromKey, toKey, skipDup, shift) {
  const txs = S.tx.filter((t) => t.month === fromKey);
  let saved = 0, skipped = 0;
  for (const t of txs) {
    if (skipDup && S.tx.some((x) => x.month === toKey && x.description === t.description && Math.round(Number(x.amount) * 100) === Math.round(Number(t.amount) * 100))) { skipped++; continue; }
    const body = { description: t.description, amount: t.amount, type: t.type, cat: t.cat, date: shift(t.date), month: toKey, source: getTxSrc(t) };
    if (t.excluded === true) body.excluded = true;
    if (t.member_id) body.member_id = t.member_id;
    try { await insertTx(body); saved++; } catch (e) { console.error(e); }
  }
  touch();
  toast('✅ הועתקו ' + saved + ' פעולות לתקופה' + (skipped ? ' (' + skipped + ' כפילויות דולגו)' : ''));
}

/* ── כפילויות ── */
export function approveDups(ids) {
  const ok = new Set((S.set.dupOk || []).map(String));
  ids.forEach((id) => ok.add(String(id)));
  saveSetting('dup_ok', [...ok]);
  toast(ids.length > 1 ? '✓ ' + ids.length + ' אושרו' : '✓ אושרה כעסקה נפרדת');
}
export function approveAllDups() {
  approveDups(findDupTx().map((x) => x.extra.id));
}

/* ── שמות שנשמרו הפוכים ── */
export async function fixReversedNames() {
  const L = revCandidates();
  if (!L.length) return;
  try {
    await Promise.all(L.map(async (x) => {
      const body = { description: x.rev };
      if (x.t.raw_name && x.t.raw_name === x.t.description) body.raw_name = x.rev;
      await q(db().from('transactions').update(body).eq('id', x.t.id));
      Object.assign(x.t, body);
    }));
    touch();
    toast('✅ תוקנו ' + L.length + ' שמות');
  } catch (e) {
    toast('⚠️ ' + e.message);
  }
}
export function keepName(id) {
  const ok = new Set((S.set.revOk || []).map(String));
  ok.add(String(id));
  saveSetting('rev_ok', [...ok]);
}

/* ══════ חיובים קבועים ══════ */

/** שמירה. אם חסרה עמודה בטבלה (source, every_months), שומר בלעדיה ומסביר */
export async function recWrite(id, body, tries = 0) {
  if (body.description) body.description = noTags(body.description);
  try {
    if (id != null) {
      await q(db().from('recurring').update(body).eq('id', id));
      const r = S.rec.find((x) => x.id === id);
      if (r) Object.assign(r, body);
      touch();
      return r;
    }
    const rows = await q(db().from('recurring').insert(body).select());
    S.rec.push(rows[0]);
    touch();
    return rows[0];
  } catch (e) {
    const col = (e.message.match(/'([a-z_]+)' column/) || [])[1];
    if (col && col in body && tries < 4) {
      const b = Object.assign({}, body);
      delete b[col];
      const what = { source: 'אשראי/עו"ש', every_months: 'התדירות', start_month: 'התדירות' }[col] || col;
      toast('⚠️ נשמר בלי ' + what + ': חסרה עמודת ' + col + ' בטבלת recurring. הרץ את supabase/schema.sql');
      return recWrite(id, b, tries + 1);
    }
    throw e;
  }
}
export async function deleteRec(id) {
  await q(db().from('recurring').delete().eq('id', id));
  S.rec = S.rec.filter((r) => r.id !== id);
  touch();
}

function saveRecMatch(M) {
  return saveSetting('rec_match', { alias: M.alias, skip: M.skip, link: M.link });
}
/** קישור ידני: העסקה הזו היא החיוב הקבוע. השם שלה נשמר, כדי שבפעם הבאה יזוהה לבד */
export function recLink(t, recId) {
  const M = S.set.recMatch;
  M.link[t.id] = recId;
  const n = mNorm(t.description).trim(), al = (M.alias[recId] = M.alias[recId] || []);
  if (n && al.indexOf(n) < 0) al.push(n);
  saveRecMatch(M);
  toast('✅ קושר. בפעם הבאה הוא יזוהה לבד');
}
export function recSkip(recId, ym) {
  const M = S.set.recMatch, s = (M.skip[recId] = M.skip[recId] || []);
  if (s.indexOf(ym) < 0) s.push(ym);
  saveRecMatch(M);
  toast('✅ החודש הוא לא ייספר');
}
export function recUnskip(recId, ym) {
  const M = S.set.recMatch;
  M.skip[recId] = (M.skip[recId] || []).filter((x) => x !== ym);
  saveRecMatch(M);
  toast('✅ בוטל');
}
export function recUnlink(txId) {
  const M = S.set.recMatch;
  delete M.link[txId];
  saveRecMatch(M);
  toast('✅ הקישור בוטל');
}

/** הפיכת סימולציה לקבוע: יום החיוב החדש נשמר בחיובים הקבועים */
export async function applySim(sim) {
  await Promise.all(Object.keys(sim).map((id) => {
    const r = S.rec.find((x) => String(x.id) === String(id));
    return r ? recWrite(r.id, { day_of_month: sim[id] }) : null;
  }));
  toast('✅ ימי החיוב עודכנו');
}

/* ══════ תשלומים עתידיים ══════ */

async function pruneCommits() {
  const old = staleCommits();
  if (!old.length) return;
  const ids = new Set(old.map((c) => c.id));
  S.commits = S.commits.filter((c) => !ids.has(c.id));
  touch();
  await Promise.all(old.map((c) => db().from('commitments').delete().eq('id', c.id)));
}
export async function deleteCommit(id) {
  S.commits = S.commits.filter((c) => c.id !== id);
  touch();
  await q(db().from('commitments').delete().eq('id', id));
  toast('🗑️ נמחק');
}
/** אחרי סריקת דוח כרטיס: מסלק התחייבויות שהתממשו, ומוסיף תשלומים עתידיים חדשים */
export async function syncCommitsFromScan(items, scan) {
  const kill = realizedCommits(items, scan.chargeDate);
  if (kill.length) {
    const ids = new Set(kill.map((c) => c.id));
    S.commits = S.commits.filter((c) => !ids.has(c.id));
    await Promise.all(kill.map((c) => db().from('commitments').delete().eq('id', c.id)));
    toast('💳 ' + kill.length + ' חיובים עתידיים התממשו והוסרו מהתחזית');
  }
  const add = commitsFromScan(scan.installments, scan.deferred);
  if (add.length) {
    try {
      const rows = await q(db().from('commitments').insert(add).select());
      S.commits.push(...rows);
      toast('💳 ' + rows.length + ' חיובים עתידיים נוספו לתחזית');
    } catch (e) {
      toast('⚠️ שמירת חיובים עתידיים נכשלה: ' + e.message);
    }
  }
  await pruneCommits();
  touch();
}

/* ══════ יעדי תקציב ══════ */
/** יעדים מהתקופה הזו והלאה. ביטול יעד שירש מתקופה קודמת נשמר כ-0, אחרת הוא היה חוזר */
export async function saveBudgets(key, values) {
  const inherited = new Set(budgetRows(key).filter((g) => g.month !== key && Number(g.amount) > 0).map((g) => g.cat));
  try {
    await Promise.all(Object.keys(values).map(async (catId) => {
      const val = values[catId] > 0 ? values[catId] : 0;
      const exist = S.budgets.find((g) => g.cat === catId && g.month === key);
      if (exist) {
        if (Number(exist.amount) === val) return;
        await q(db().from('budget_goals').update({ amount: val }).eq('id', exist.id));
        exist.amount = val;
      } else if (val > 0 || inherited.has(catId)) {
        const rows = await q(db().from('budget_goals').insert({ cat: catId, month: key, amount: val }).select());
        S.budgets.push(rows[0]);
      }
    }));
    // תקופה שקיבלה יעד אחד חדש צריכה גם את שאר היעדים שירשה, אחרת הם ייעלמו בה
    const own = new Set(S.budgets.filter((g) => g.month === key).map((g) => g.cat));
    const missing = [...inherited].filter((c) => !own.has(c) && !(c in values));
    if (missing.length) {
      const prev = budgetRows(keyAdd(key, -1));
      const rows = await q(db().from('budget_goals').insert(missing.map((c) => ({ cat: c, month: key, amount: prev.find((g) => g.cat === c).amount }))).select());
      S.budgets.push(...rows);
    }
    toast('✅ יעדים נשמרו. הם ימשיכו גם לתקופות הבאות');
  } catch (e) {
    toast('⚠️ היעדים לא נשמרו: ' + e.message);
  }
  touch();
}

/* ══════ יעדי חיסכון ══════ */
export async function saveSavingsGoal(id, body, cfg) {
  body.name = noTags(body.name);
  let g;
  if (id != null) {
    await q(db().from('savings_goals').update(body).eq('id', id));
    g = S.savings.find((x) => x.id === id);
    Object.assign(g, body);
  } else {
    const rows = await q(db().from('savings_goals').insert(Object.assign({ color: COLORS[S.savings.length % COLORS.length] }, body)).select());
    g = rows[0];
    S.savings.push(g);
  }
  await saveSetting('sav_cfg', Object.assign({}, S.set.savCfg, { [g.id]: cfg }));
  toast('✅ היעד נשמר');
}
export async function addToSavingsGoal(id, amt) {
  const g = S.savings.find((x) => x.id === id);
  if (!g) return;
  const v = Number(g.saved) + amt;
  await q(db().from('savings_goals').update({ saved: v }).eq('id', id));
  g.saved = v;
  touch();
  toast('✅ ' + fMoney(amt) + ' נוסף ליעד');
}
export async function deleteSavingsGoal(id) {
  await q(db().from('savings_goals').delete().eq('id', id));
  S.savings = S.savings.filter((g) => g.id !== id);
  const cfg = Object.assign({}, S.set.savCfg);
  delete cfg[id];
  await saveSetting('sav_cfg', cfg);
  toast('🗑️ נמחק');
}
/** שיוך העברה ליעד: מוסיף ליעד מילה מהתיאור שלה, כך שגם ההעברות הבאות ישויכו לבד */
export function assignToSavingsGoal(t, goalId) {
  const n = mNorm(t.description || '').trim(), m = n.match(SAV_RX), kw = m ? m[0] : n;
  const all = Object.assign({}, S.set.savCfg);
  const c = Object.assign({}, all[goalId] || {});
  const list = String(c.kw || '').split(',').map((x) => x.trim()).filter(Boolean);
  if (list.indexOf(kw) < 0) list.push(kw);
  c.kw = list.join(', ');
  if (!c.from || iso10(t.date) < c.from) c.from = iso10(t.date);
  all[goalId] = c;
  saveSetting('sav_cfg', all);
  toast('✅ שויך. העברות עם "' + kw + '" ייספרו מעכשיו ביעד הזה');
}

/* ══════ קטגוריות ══════ */
export async function addCategory({ label, icon, color, type, keywords, id }) {
  const c = { id: id || 'c_' + Date.now(), label: noTags(label), icon: noTags(icon) || '📦', color: color || '#bdbdbd', type };
  if (keywords) c.keywords = keywords;
  await q(db().from('categories').insert(c));
  S.cats.push(c);
  touch();
  return c;
}
export async function deleteCategory(id) {
  await q(db().from('categories').delete().eq('id', id));
  S.cats = S.cats.filter((c) => c.id !== id);
  touch();
}

/* ══════ תיקונים חד פעמיים לנתונים ישנים ══════ */

/** סימונים ישנים ששמורים רק בדפדפן (מקור אשראי, החרגות) עוברים לבסיס הנתונים */
async function migrateLocalFlags() {
  let srcMap = {}, excl = [];
  try { srcMap = JSON.parse(ls.get('tx_source_map', '{}')) || {}; excl = JSON.parse(ls.get('excluded_tx_ids', '[]')) || []; } catch { return; }
  const jobs = [];
  Object.keys(srcMap).forEach((id) => {
    const t = S.tx.find((x) => String(x.id) === String(id));
    if (!t || t.source === srcMap[id] || (t.source && t.source !== 'cash')) return;
    t.source = srcMap[id];
    jobs.push(q(db().from('transactions').update({ source: srcMap[id] }).eq('id', t.id)));
  });
  excl.forEach((id) => {
    const t = S.tx.find((x) => String(x.id) === String(id));
    if (!t || t.excluded === true) return;
    t.excluded = true;
    jobs.push(q(db().from('transactions').update({ excluded: true }).eq('id', t.id)));
  });
  if (!jobs.length) return;
  try {
    await Promise.all(jobs);
    localStorage.removeItem('tx_source_map');
    localStorage.removeItem('excluded_tx_ids');
    touch();
    toast('✅ ' + jobs.length + ' סימונים הועברו לענן');
  } catch (e) {
    console.error('migration failed', e);
  }
}

/* גרסאות קודמות שמרו את חיוב הכרטיס מקובץ העו"ש כהוצאה רגילה, ואז הוא נספר פעמיים:
   פעם כעסקאות האשראי ופעם כשורת החיוב בעו"ש. מסמנים אותו כפנימי, אבל רק כשעסקאות הכרטיס
   של אותו מחזור כבר נסרקו (אחרת שורת החיוב היא הרישום היחיד של ההוצאה) */
async function fixCardSettlements() {
  if (S.set.fixCardSettle) return;
  const sums = cardPeriodSums();
  const L = S.tx.filter((t) => {
    if (!(getTxSrc(t) !== 'credit' && t.type === 'expense' && t.excluded !== true && absAmt(t) >= 500 && CARD_RX.test(t.description || ''))) return false;
    const ds = iso10(t.date);
    if (!ds) return false;
    const k = getKey(new Date(cardEffISO(t, ds) + 'T12:00:00'));
    return (sums[k] || 0) > 0 || (sums[keyAdd(k, -1)] || 0) > 0;
  });
  await saveSetting('fix_card_settle_v2', '1');
  if (!L.length) return;
  try {
    await Promise.all(L.map((t) => { t.excluded = true; return q(db().from('transactions').update({ excluded: true }).eq('id', t.id)); }));
    touch();
    toast('✅ ' + L.length + ' חיובי כרטיס בעו"ש סומנו כפנימיים, כדי שלא ייספרו פעמיים בהוצאות');
  } catch (e) {
    console.error(e);
  }
}

/* ══════ חשבון ══════ */
const APP_LOCAL_KEYS = ['flow_per', 'set_open', 'dash_more', 'hide_fixed', 'last_backup', 'tx_source_map', 'excluded_tx_ids'];
export async function logout() {
  try { await db().auth.signOut(); } catch { /* ממשיכים בכל מקרה */ }
  APP_LOCAL_KEYS.forEach((k) => localStorage.removeItem(k));
  location.reload();
}

